import { computed, onMounted, reactive, ref, watch } from 'vue'
import type { AdminBook, AdminEarning, AdminUser, CreatorBalance } from '@/types/admin'
import type { Pagination } from '@/types/user'

const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api/v1'
const emptyPagination = (): Pagination => ({ page: 1, limit: 20, total: 0, pages: 0 })

export const useAdminDashboard = () => {
  const activeTab = ref<'reviews' | 'appeals' | 'earnings'>('reviews')
  const loading = ref(false)
  const actingBookId = ref<number | null>(null)
  const actingEarningId = ref<number | null>(null)
  const message = ref('')
  const error = ref('')
  const reviews = ref<AdminBook[]>([])
  const appeals = ref<AdminBook[]>([])
  const balances = ref<CreatorBalance[]>([])
  const earnings = ref<AdminEarning[]>([])
  const actionReasons = reactive<Record<number, string>>({})
  const adminToken = ref(localStorage.getItem('adminToken') || 'MUSE_ADMIN_2024')
  const adminPassword = ref(localStorage.getItem('adminPassword') || 'pineapple_honey_2024')
  const reviewPagination = ref<Pagination>(emptyPagination())
  const appealPagination = ref<Pagination>(emptyPagination())
  const balancePagination = ref<Pagination>(emptyPagination())
  const earningPagination = ref<Pagination>(emptyPagination())

  const currentBooks = computed(() => activeTab.value === 'reviews' ? reviews.value : appeals.value)
  const currentPagination = computed(() => activeTab.value === 'reviews' ? reviewPagination.value : appealPagination.value)

  const saveCredentials = () => {
    localStorage.setItem('adminToken', adminToken.value)
    localStorage.setItem('adminPassword', adminPassword.value)
    message.value = '管理员凭证已保存'
    error.value = ''
  }

  const adminRequest = async <T>(path: string, options: RequestInit = {}) => {
    const response = await fetch(`${apiBaseUrl}${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'x-admin-token': adminToken.value,
        'x-admin-password': adminPassword.value,
        ...(options.headers || {})
      }
    })

    const payload = await response.json().catch(() => null)
    if (!response.ok || payload?.code >= 400) {
      throw new Error(payload?.message || payload?.error || '请求失败')
    }

    return (payload.data ?? payload) as T
  }

  const loadReviews = async () => {
    const data = await adminRequest<{ items: AdminBook[]; pagination: Pagination }>('/admin/reviews')
    reviews.value = data.items
    reviewPagination.value = data.pagination
  }

  const loadAppeals = async () => {
    const data = await adminRequest<{ items: AdminBook[]; pagination: Pagination }>('/admin/appeals')
    appeals.value = data.items
    appealPagination.value = data.pagination
  }

  const loadBalances = async () => {
    const data = await adminRequest<{ items: CreatorBalance[]; pagination: Pagination }>('/admin/creator-balances')
    balances.value = data.items
    balancePagination.value = data.pagination
  }

  const loadEarnings = async () => {
    const data = await adminRequest<{ items: AdminEarning[]; pagination: Pagination }>('/admin/creator-earnings')
    earnings.value = data.items
    earningPagination.value = data.pagination
  }

  const refreshData = async () => {
    try {
      loading.value = true
      error.value = ''
      await Promise.all([loadReviews(), loadAppeals(), loadBalances(), loadEarnings()])
    } catch (err: any) {
      error.value = err.message || '加载失败，请检查管理员凭证'
    } finally {
      loading.value = false
    }
  }

  const runAction = async (book: AdminBook, action: 'approve' | 'reject') => {
    const isAppeal = activeTab.value === 'appeals'
    const reason = actionReasons[book.id]?.trim() || ''

    if (action === 'reject' && reason.length < 2) {
      error.value = '拒绝时请填写至少2个字的原因'
      return
    }

    try {
      actingBookId.value = book.id
      error.value = ''
      message.value = ''
      const path = isAppeal
        ? `/admin/appeals/${book.id}/${action}`
        : `/admin/reviews/${book.id}/${action}`

      await adminRequest<{ book: AdminBook }>(path, {
        method: 'POST',
        body: JSON.stringify({ reason })
      })

      message.value = action === 'approve'
        ? isAppeal ? '申诉已通过，作品已发布' : '作品已审核通过并发布'
        : isAppeal ? '申诉已拒绝' : '作品已拒绝'
      actionReasons[book.id] = ''
      await refreshData()
    } catch (err: any) {
      error.value = err.message || '操作失败'
    } finally {
      actingBookId.value = null
    }
  }

  const approveBook = (book: AdminBook) => runAction(book, 'approve')
  const rejectBook = (book: AdminBook) => runAction(book, 'reject')

  const runEarningAction = async (earning: AdminEarning, action: 'freeze' | 'settle' | 'reverse') => {
    try {
      actingEarningId.value = earning.id
      error.value = ''
      message.value = ''
      await adminRequest<{ earning: AdminEarning }>(`/admin/creator-earnings/${earning.id}/${action}`, {
        method: 'POST',
        body: JSON.stringify({})
      })
      message.value = action === 'freeze' ? '收益已冻结' : action === 'settle' ? '收益已恢复入账' : '收益已冲正'
      await Promise.all([loadBalances(), loadEarnings()])
    } catch (err: any) {
      error.value = err.message || '收益操作失败'
    } finally {
      actingEarningId.value = null
    }
  }

  const formatUser = (user?: AdminUser | null) => {
    if (!user) return '未知用户'
    return user.nickname || user.phone || `用户 ${user.id}`
  }

  const formatOwner = (book: AdminBook) => {
    if (!book.owner) return book.ownerUserId ? `用户 ${book.ownerUserId}` : '未知'
    return book.owner.nickname || book.owner.phone || `用户 ${book.owner.id}`
  }

  const formatDate = (value?: string) => {
    if (!value) return '-'
    return new Date(value).toLocaleString('zh-CN', {
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const statusText = (book: AdminBook) => {
    if (book.appealStatus === 'pending') return '申诉待复审'
    if (book.reviewStatus === 'pending') return '待人工审核'
    if (book.reviewStatus === 'rejected') return '未通过'
    return '已通过'
  }

  const customRatio = (book: AdminBook) => {
    if (!book.totalChoiceCount) return 0
    return Math.round(((book.customChoiceCount || 0) / book.totalChoiceCount) * 100)
  }

  const earningStatusText = (status: AdminEarning['status']) => {
    if (status === 'pending') return '待结算'
    if (status === 'settled') return '已入账'
    if (status === 'frozen') return '已冻结'
    if (status === 'reversed') return '已冲正'
    return status
  }

  const earningStatusClass = (status: AdminEarning['status']) => {
    if (status === 'pending') return 'bg-amber-50 text-amber-700'
    if (status === 'settled') return 'bg-leaf-50 text-leaf-700'
    if (status === 'frozen') return 'bg-orange-50 text-orange-700'
    if (status === 'reversed') return 'bg-red-50 text-red-600'
    return 'bg-ink-50 text-ink-500'
  }

  watch(activeTab, () => {
    message.value = ''
    error.value = ''
  })

  onMounted(() => {
    refreshData()
  })

  return {
    activeTab,
    loading,
    actingBookId,
    actingEarningId,
    message,
    error,
    reviews,
    appeals,
    balances,
    earnings,
    actionReasons,
    adminToken,
    adminPassword,
    reviewPagination,
    appealPagination,
    balancePagination,
    earningPagination,
    currentBooks,
    currentPagination,
    saveCredentials,
    refreshData,
    approveBook,
    rejectBook,
    runEarningAction,
    formatUser,
    formatOwner,
    formatDate,
    statusText,
    customRatio,
    earningStatusText,
    earningStatusClass
  }
}
